import { prefersReducedMotion } from "@theme/utilities";

/**
 * Applies a subtle parallax effect to the graphic element as the user scrolls.
 * Respects prefers-reduced-motion.
 */
class ParallaxGraphicElement extends HTMLElement {
  #scrollListener = () => this.#update();
  #intersectionObserver = null;

  connectedCallback() {
    if (prefersReducedMotion()) return;

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;

      if (entry.isIntersecting) {
        window.addEventListener("scroll", this.#scrollListener, { passive: true });
        this.#update();
      } else {
        window.removeEventListener("scroll", this.#scrollListener);
      }
    });

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    window.removeEventListener("scroll", this.#scrollListener);
  }

  #update() {
    const isMobile = window.innerWidth < 1024;
    const speed = parseFloat(
      isMobile
        ? (this.dataset.parallaxSpeedMobile ?? this.dataset.parallaxSpeed ?? "0.15")
        : (this.dataset.parallaxSpeed ?? "0.15")
    );
    const rect = this.getBoundingClientRect();
    const viewH = window.innerHeight;
    const progress = (viewH / 2 - (rect.top + rect.height / 2)) / viewH;
    this.style.transform = `translateY(${progress * speed * 200}px)`;
  }
}

if (!customElements.get("parallax-graphic-element")) {
  customElements.define("parallax-graphic-element", ParallaxGraphicElement);
}

export default ParallaxGraphicElement;
